import React, { useEffect, useState } from 'react';
import { Card, Form, Select, Button, Row, Col } from 'antd';
import { toast } from 'react-toastify';
import leadProspectApi from '../../api/leadProspectApi';
import { GetColumnSearchAndSortProps } from "../../components/Utils/GetColumnSearchAndSortProps";
import ColumnManagerTable from "../../components/Utils/ColumnManagerTable";

const { Option } = Select;

const FILTER_FIELDS = [
    { key: 'country', label: 'Countries' },
    { key: 'jobFunction', label: 'Job Function' },
    { key: 'jobLevel', label: 'Job Seniority' },
    { key: 'industry', label: 'Industry' },
    { key: 'companyEmployeeSize', label: 'Company Size' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'source', label: 'Data Source' },
];

const EmployeeEnrichPage = () => {

    const [form] = Form.useForm();

    const [filtersData, setFiltersData] = useState({});
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // =========================
    // LOAD FILTERS
    // =========================
    const fetchAllFilters = async () => {

        const fieldsPayload = FILTER_FIELDS.map(field => {

            const item = {
                fieldName: field.key
            };

            // Only pass topK for these fields
            if (
                field.key === "source" ||
                field.key === "jobTitle" ||
                field.key === "companyName" ||
                field.key === "companyDomain"
            ) {
                item.topK = 100;
            }

            return item;
        });

        const res = await leadProspectApi.getDistinctValues(fieldsPayload);

        if (!res.success) {
            toast.error('Failed to load filters');
            return;
        }

        const data = res.data.data;

        const temp = {};

        FILTER_FIELDS.forEach(field => {
            temp[field.key] = data[field.key] || [];
        });
        setFiltersData(temp);
    };

    // =========================
    // FETCH LEADS
    // =========================
    const fetchLeads = async (payload = {}) => {

        setLoading(true);

        const requestPayload = { ...payload };

        const res = await leadProspectApi.searchEmployeeLeads(requestPayload);

        if (res.success) {

            setResults(res.data.data.content);

            setPagination({
                current: res.data.data.page,
                pageSize: res.data.data.size,
                total: res.data.data.total
            });

        } else {
            toast.error(res.message || 'Failed to load leads');
        }

        setLoading(false);
    };

    // =========================
    // INITIAL LOAD
    // =========================
    useEffect(() => {
        fetchAllFilters();
        fetchDefaultLeads();
    }, []);

    // =========================
    // DEFAULT LEADS
    // =========================
    const fetchDefaultLeads = async () => {

        setLoading(true);

        const res = await leadProspectApi.searchEmployeeLeads({
            page: 1,
            size: 10
        });

        if (res.success) {

            setResults(res.data.data.content);

            setPagination({
                current: res.data.data.page,
                pageSize: res.data.data.size,
                total: res.data.data.total
            });

        } else {
            toast.error(res.message || 'Failed to load leads');
        }

        setLoading(false);
    };

    // =========================
    // BUILD PAYLOAD
    // =========================
    const buildPayload = (values) => {

        const icpFilters = {};
        let domains = [];
        let topK;

        Object.keys(values).forEach((key) => {

            const value = values[key];

            if (!value || (Array.isArray(value) && value.length === 0)) {
                return;
            }

            if (key === 'companyDomain') {

                domains = value;

            } else if (key === 'topK') {

                topK = value;

            } else if (key === 'country') {

                icpFilters['countries'] = value;

            } else if (key === 'companyEmployeeSize') {

                icpFilters['companySize'] = value;

            } else if (key === 'jobLevel') {

                icpFilters['jobSeniority'] = value;

            } else {

                icpFilters[key] = value;
            }
        });

        return {
            ...(Object.keys(icpFilters).length > 0 && { icpFilters }),
            ...(domains.length > 0 && { domains }),
            ...(topK !== undefined && { topK }),
        };
    };

    // =========================
    // SEARCH
    // =========================
    const handleSearch = async () => {

        const values = form.getFieldsValue();

        const payload = buildPayload(values);

        payload.page = 1;
        payload.size = pagination.pageSize;

        await fetchLeads(payload);
    };

    // =========================
    // TABLE COLUMNS
    // =========================
    const columnsConfig = [
        {
            key: "firstName",
            title: "First Name",
            checked: true,
            column: {
                title: "First Name",
                dataIndex: "firstName",
                key: "firstName",
                ...GetColumnSearchAndSortProps("firstName", 'First Name', 'string')
            }
        },
        {
            key: "lastName",
            title: "Last Name",
            checked: true,
            column: {
                title: "Last Name",
                dataIndex: "lastName",
                key: "lastName"
            }
        },
        {
            key: "companyName",
            title: "Company Name",
            checked: true,
            column: {
                title: "Company Name",
                dataIndex: "companyName",
                key: "companyName"
            }
        },
        {
            key: "phone",
            title: "Mobile Number",
            checked: true,
            column: {
                title: "Mobile Number",
                dataIndex: "phone",
                key: "phone"
            }
        },
        {
            key: "companyPhone",
            title: "Company Phone",
            checked: true,
            column: {
                title: "Company Phone",
                dataIndex: "companyPhone",
                key: "companyPhone"
            }
        },
        {
            key: "linkedinUrl",
            title: "LinkedIn Url",
            checked: true,
            column: {
                title: "LinkedIn Url",
                dataIndex: "linkedinUrl",
                key: "linkedinUrl"
            }
        }
    ];

    return (
        <div style={{ padding: 20 }}>

            {/* =========================
                FILTER CARD
            ========================= */}
            <Card
                title="Employee Leads Search (Dynamic Filters)"
                style={{ marginBottom: 20 }}
            >

                <Form form={form} layout="vertical">

                    <Row gutter={16}>

                        {FILTER_FIELDS.map(field => (

                            <Col span={8} key={field.key}>

                                <Form.Item
                                    name={field.key}
                                    label={field.label}
                                >

                                    <Select
                                        mode="multiple"
                                        placeholder={`Select ${field.label}`}
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                    >

                                        {filtersData[field.key]?.map(val => (
                                            <Option key={val} value={val}>
                                                {val}
                                            </Option>
                                        ))}

                                    </Select>

                                </Form.Item>

                            </Col>
                        ))}

                    </Row>

                    <Button
                        type="primary"
                        onClick={handleSearch}
                        loading={loading}
                    >
                        Search Employee Leads
                    </Button>

                </Form>

            </Card>

            {/* =========================
                RESULTS CARD
            ========================= */}
            <Card
                title={
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <span>Results</span>

                        <span
                            style={{
                                fontWeight: 600,
                                color: '#1677ff'
                            }}
                        >
                            Total Employees: {pagination.total}
                        </span>
                    </div>
                }
            >

                <ColumnManagerTable
                    pageKey="employee_enrich"
                    columnsConfig={columnsConfig}
                    data={results}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true
                    }}
                    onChange={(paginationInfo) => {

                        const values = form.getFieldsValue();

                        const payload = buildPayload(values);

                        payload.page = paginationInfo.current;
                        payload.size = paginationInfo.pageSize;

                        fetchLeads(payload);
                    }}
                />

            </Card>

        </div>
    );
};

export default EmployeeEnrichPage;